// FIXME this file is considered a temporary workaround for GlobalTags getting out of sync with the Documents table.
// Once we improve the stability of GlobalTags, we should get rid of this whole file.

import type { MangoQuery } from 'rxdb';

import type Database from '../database/Database';
import { makeDocsWithTagNameQuery } from '../database/queryHelpers';
import { documentHasTagsQuery, isFirstClassDocumentQuery } from '../database/standardQueries';
import type { GlobalTag } from '../types/tags';
import nowTimestamp from '../utils/dates/nowTimestamp';
import delay from '../utils/delay';
// eslint-disable-next-line import/no-cycle
import exceptionHandler from '../utils/exceptionHandler.platform';
import makeLogger from '../utils/makeLogger';
import { Cache } from './cache.platform';
import { getCacheItem, setCacheItem } from './methods';

const logger = makeLogger(__filename);

// Configuration
const REFRESH_RUN_INTERVAL_MS = 10 * 60 * 1000; // keep a 10 minute delay between runs
const INITIAL_DELAY_MS = 10 * 1000; // wait 10 seconds before running the first refresh
const DEFAULT_BATCH_SIZE = 10; // how often to commit the changes to the database
const TAG_PROCESSING_DELAY_MS = 1000; // how long to wait between processing each tag (1 second), higher value reduces CPU usage
const TAG_REFRESH_PERIOD_MS = 7 * 24 * 60 * 60 * 1000; // how long to wait before refreshing a tag again (7 days)
const MAX_TAGS_PER_RUN = 50; // hard cap on number of tags refreshed per single run

let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Refresh GlobalTags by querying the local database directly.
 * This should be run periodically to ensure the GlobalTags stay accurate,
 * especially on desktop where counts tend to drift over time.
 *
 * Eventually we would like to find the root causes of the divergence between cached GlobalTags and the Document data,
 * but that requires dev bandwidth we currently don't have (Oct 2025).
 *
 * We find all tags that haven't been refreshed recently (TAG_REFRESH_PERIOD_MS), and refresh them in batches (DEFAULT_BATCH_SIZE).
 * We then persist the refresh timestamp in the cache.
 * We then schedule the next refresh (REFRESH_RUN_INTERVAL_MS).
 */
export async function refreshGlobalTags(
  database: Database,
  options: {
    batchSize?: number; // Maximum number of tags to refresh in one batch
  } = {},
): Promise<void> {
  const { batchSize = DEFAULT_BATCH_SIZE } = options;

  if (!database.isInitialized()) {
    logger.warn('Database is not initialized, skipping GlobalTags refresh');
    return;
  }

  try {
    const allGlobalTags = await database.collections.global_tags.findAll();
    const tagRefreshCache = Cache.createInstance({ name: 'globalTagRefresh' });
    const threshold = nowTimestamp() - TAG_REFRESH_PERIOD_MS;

    // get due tags by reading their cached timestamps
    const tagKeys = allGlobalTags.map((t) => `gtr/${t.id}`);
    const cachedTimestamps = await tagRefreshCache.getItems<number>(tagKeys);
    const withTimestamps = allGlobalTags.map((tag) => {
      const key = `gtr/${tag.id}`;
      const ts = cachedTimestamps[key] ?? 0;
      return { tag, ts };
    });

    const due = withTimestamps
      .filter((x) => x.ts <= threshold)
      .sort((a, b) => a.ts - b.ts);

    // Only refresh up to MAX_TAGS_PER_RUN least recently refreshed tags
    const toProcess = due.slice(0, MAX_TAGS_PER_RUN);

    const totalTags = allGlobalTags.length;
    const refreshedTags = withTimestamps.filter((x) => x.ts > threshold).length;
    const dueTags = due.length;

    if (due.length === 0) {
      logger.debug('No GlobalTags due for refresh', {
        totalTags,
        refreshedTags,
        dueTags: 0,
      });
      return;
    }

    logger.debug(`GlobalTags refresh progress: ${refreshedTags}/${totalTags} refreshed, ${dueTags} due, ${toProcess.length} to process in this run`, {
      totalTags,
      refreshedTags,
      dueTags,
    });

    // process in batches of `batchSize` with delays between each tag
    for (let start = 0; start < toProcess.length; start += batchSize) {
      const batch = toProcess.slice(start, start + batchSize);
      logger.debug(`Refreshing batch of ${batch.length} tags (start=${start})`);

      const batchTimestamp = nowTimestamp();
      const updatedTags: GlobalTag[] = [];
      const processedTagIds: string[] = [];
      for (const { tag } of batch) {
        try {
          const updatedTag = await refreshSingleTag(database, tag);
          if (updatedTag) {
            updatedTags.push(updatedTag);
          }
          processedTagIds.push(tag.id);
          await delay(TAG_PROCESSING_DELAY_MS);
        } catch (error) {
          const errorMessage = `Error refreshing tag`;
          logger.error(errorMessage, { error });
          exceptionHandler.captureException(error instanceof Error ? error : new Error(String(error)), {
            extra: { errorMessage, tagName: tag.name, tagId: tag.id },
          });
        }
      }

      if (updatedTags.length > 0) {
        await database.collections.global_tags.bulkUpsert(updatedTags, {
          eventName: 'global-tags-refreshed',
          isUndoable: false,
          shouldNotSendPersistentChangesToServer: false,
          userInteraction: null,
        });
        logger.debug(`Refreshed counts for ${updatedTags.length} GlobalTags`);
      }

      // persist refresh timestamp in cache after successful database update
      for (const tagId of processedTagIds) {
        await tagRefreshCache.setItem(`gtr/${tagId}`, batchTimestamp);
      }
    }
  } catch (error) {
    const errorMessage = 'Error in refreshGlobalTags';
    logger.error(errorMessage, { error });
    exceptionHandler.captureException(error instanceof Error ? error : new Error(String(error)), {
      extra: { errorMessage, batchSize },
    });
    throw error;
  }
}

/* Refresh a single GlobalTag by querying the local database directly */
async function refreshSingleTag(
  database: Database,
  tag: GlobalTag,
): Promise<GlobalTag | null> {
  // query to find documents with this tag
  const tagQuery = makeDocsWithTagNameQuery(tag.name);

  // count highlights with this tag (category === Category.Highlight)
  const highlightsQuery: MangoQuery = {
    ...tagQuery,
    selector: {
      ...tagQuery.selector,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'rxdbOnly.indexFields.triage_status_exists': 0,
    },
  };
  // count first-class documents (non-highlights)
  const firstClassDocumentsQuery: MangoQuery = {
    ...tagQuery,
    selector: {
      ...tagQuery.selector,
      ...isFirstClassDocumentQuery,
    },
  };

  const [highlightsCount, firstClassDocumentsCount] = await Promise.all([
    database.collections.documents.count(highlightsQuery),
    database.collections.documents.count(firstClassDocumentsQuery),
  ]);

  // derive total count (sum of both categories)
  const totalCount = highlightsCount + firstClassDocumentsCount;

  // get the most recent assignment time from documents with this tag
  // only fetch if totalCount changed (most common case where it's unchanged)
  let newLastAssignedAt = tag.lastAssignedAt;
  const tagId = tag.id.toLowerCase();
  const sortKey = `tags."${tagId}".created`;
  const docs = await database.collections.documents.find({
    selector: tagQuery.selector,
    sort: [
      // claude eslint: sortKey is a dynamic nested path (e.g. `tags."tagid".created`) that cannot be
      // represented as a static RxDB sort type; `as never` satisfies the typed sort tuple constraint.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        [sortKey]: 'desc',
      } as never,
    ],
    limit: 1,
  });
  if (docs.length) {
    const doc = docs[0];
    const docTag = doc.tags?.[tagId];
    if (docTag?.created) {
      newLastAssignedAt = docTag.created;
    }
  }

  // create updated tag with new counts and lastAssignedAt
  const updatedTag: GlobalTag = {
    ...tag,
    totalCount,
    highlightsCount,
    firstClassDocumentsCount,
    lastAssignedAt: newLastAssignedAt,
  };

  // only update if counts or lastAssignedAt have changed
  if (
    updatedTag.totalCount !== tag.totalCount ||
    updatedTag.highlightsCount !== tag.highlightsCount ||
    updatedTag.firstClassDocumentsCount !== tag.firstClassDocumentsCount ||
    updatedTag.lastAssignedAt !== tag.lastAssignedAt
  ) {
    const before = {
      totalCount: tag.totalCount,
      highlightsCount: tag.highlightsCount,
      firstClassDocumentsCount: tag.firstClassDocumentsCount,
      lastAssignedAt: tag.lastAssignedAt,
    };
    const after = {
      totalCount: updatedTag.totalCount,
      highlightsCount: updatedTag.highlightsCount,
      firstClassDocumentsCount: updatedTag.firstClassDocumentsCount,
      lastAssignedAt: updatedTag.lastAssignedAt,
    };
    logger.debug(`Tag "${tag.name}" counts or lastAssignedAt changed:`, { before, after });
    exceptionHandler.captureMessage('GlobalTag refresh detected divergence', {
      level: 'error',
      extra: {
        tagName: tag.name,
        tagId: tag.id,
        before,
        after,
      },
    });
    return updatedTag;
  }

  return null;
}

const REBUILD_CACHE_KEY = 'lastAssignedAtFix/v1';

/**
 * One-time fix for wrong `lastAssignedAt` values on existing global_tags (bug fixed in ad19550).
 *
 * Iterates all local documents to compute the correct max `tags[tagId].created` per tag,
 * then patches only the global_tags whose `lastAssignedAt` differs.
 *
 * Gated by a cache key so it only runs once per client.
 *
 * TODO: remove this after a few weeks after the fix is deployed
 */
export async function fixAllGlobalTagsLastUpdatedAt(database: Database): Promise<void> {
  if (await getCacheItem(REBUILD_CACHE_KEY)) {
    return;
  }

  logger.debug('fixAllGlobalTagsLastUpdatedAt: starting one-time lastAssignedAt fix');

  const allDocs = await database.collections.documents.find({ selector: documentHasTagsQuery });

  const correctLastAssignedAt: { [key: string]: number; } = {};
  for (const doc of allDocs) {
    if (!doc.tags) {
      continue;
    }
    for (const [tagId, docTag] of Object.entries(doc.tags)) {
      if (docTag.created) {
        correctLastAssignedAt[tagId] = Math.max(correctLastAssignedAt[tagId] ?? 0, docTag.created);
      }
    }
  }

  const allGlobalTags = await database.collections.global_tags.findAll();
  const tagsToUpdate: GlobalTag[] = [];

  for (const tag of allGlobalTags) {
    const correct = correctLastAssignedAt[tag.id];
    if (correct != null && correct !== tag.lastAssignedAt) {
      tagsToUpdate.push({ ...tag, lastAssignedAt: correct });
    }
  }

  if (tagsToUpdate.length > 0) {
    await database.collections.global_tags.bulkUpsert(tagsToUpdate, {});
    logger.debug(`fixAllGlobalTagsLastUpdatedAt: fixed lastAssignedAt on ${tagsToUpdate.length} tags`);
  }

  await setCacheItem(REBUILD_CACHE_KEY, nowTimestamp());
  logger.debug('fixAllGlobalTagsLastUpdatedAt: done');
}

/**
 * Set up periodic refresh of GlobalTags.
 * Only runs on desktop app.
 */
export async function setupPeriodicGlobalTagsRefresh(database: Database): Promise<void> {
  // if the sync has already been set up, exit early
  if (refreshTimeoutId) {
    return;
  }

  logger.debug('Setting up periodic GlobalTags refresh');

  // schedule the first refresh after initial delay
  refreshTimeoutId = setTimeout(async () => {
    try {
      await refreshGlobalTags(database);
    } catch (error) {
      logger.error('Error in periodic GlobalTags refresh:', { error });
    }

    // schedule subsequent refreshes
    const scheduleNextRefresh = () => {
      refreshTimeoutId = setTimeout(async () => {
        try {
          await refreshGlobalTags(database);
        } catch (error) {
          logger.error('Error in periodic GlobalTags refresh:', { error });
        }
        scheduleNextRefresh();
      }, REFRESH_RUN_INTERVAL_MS);
    };

    scheduleNextRefresh();
  }, INITIAL_DELAY_MS);
}
